// Central data source for every service — used by the Services overview grid
// AND by each service's own dedicated page (pages/services/*.jsx).
// Add a new service by adding one object here + one page file + one route in App.jsx.

export const servicesData = [
  {
    slug: 'website-development',
    title: 'Website Development',
    tagline: 'Fast, modern websites built to convert',
    category: 'Web Engineering',
    tags: ['Custom Design', 'CMS Setup', 'Speed Optimised', 'Mobile-First'],
    icon: 'globe',
    img: '/images/services/website-development.jpg',
    desc: 'Fast, mobile-first websites — 50+ delivered, live in weeks, loading in under 2 seconds.',
    intro: 'We craft modern, fast, and fully responsive websites tailored to your business goals. From sleek corporate sites to powerful eCommerce platforms, our development team builds digital experiences that convert.',
    stats: [
      { value: '50+', label: 'Sites Delivered' },
      { value: '<2s', label: 'Avg Load Time' },
      { value: '100%', label: 'Mobile Responsive' },
    ],
    features: [
      'Custom business websites with unique, branded designs',
      'eCommerce stores with secure payment gateways',
      'Progressive web apps for mobile-first experiences',
      'CMS integration (WordPress, Shopify, custom solutions)',
      'Performance optimization for blazing-fast load times',
      'Ongoing maintenance and technical support',
    ],
    benefits: [
      'Increase credibility with a professional online presence',
      'Convert more visitors into paying customers',
      'Rank higher on search engines with optimized code',
      'Scale easily as your business grows',
    ],
  },
  {
    slug: 'shopify-ecommerce',
    title: 'Shopify eCommerce',
    tagline: 'Mobile-first online stores built to sell',
    category: 'Commerce',
    tags: ['Store Setup', 'Payment Gateway', 'Checkout UX', 'App Integrations'],
    icon: 'cart',
    img: '/images/services/shopify-ecommerce.jpg',
    desc: 'Shopify stores built for speed and conversion — live in 3–4 weeks, fully handed over to you.',
    intro: 'We design and build Shopify storefronts that are fast, mobile-first, and easy for you to manage. From product catalogue setup to payment gateways and checkout optimisation, we hand over a store you fully own — no lock-in, no dependency.',
    stats: [
      { value: '3-4wk', label: 'Store Launch' },
      { value: '100%', label: 'Full Ownership' },
      { value: '<2s', label: 'Avg Load Time' },
    ],
    features: [
      'Shopify store setup, theme customization & branding',
      'Product catalogue, collections & inventory setup',
      'Secure payment gateway & checkout integration',
      'Mobile-first, conversion-optimised storefronts',
      'App integrations for reviews, upsells & loyalty',
      'Store training, documentation & full handover',
    ],
    benefits: [
      'Launch a professional online store without technical dependency',
      'Convert more visitors with a fast, mobile-optimised checkout',
      'Own your store fully with no recurring dev lock-in',
      'Scale easily as your product catalogue grows',
    ],
  },
  {
    slug: 'digital-marketing',
    title: 'Digital Marketing',
    tagline: 'Data-driven growth across every channel',
    category: 'Growth Engine',
    tags: ['Google & Meta Ads', 'SEO', 'Content Strategy', 'Analytics'],
    icon: 'trending',
    img: '/images/services/digital-marketing.jpg',
    desc: 'Google, Meta & SEO campaigns built for ROI — 3.2× average return, 40% lead growth.',
    intro: 'Our data-driven marketing strategies are designed to maximize your reach, engagement, and conversions. We combine creative thinking with analytics to deliver campaigns that generate real business results.',
    stats: [
      { value: '3.2×', label: 'Avg ROI Increase' },
      { value: '15K+', label: 'Monthly Ad Reach' },
      { value: '40%', label: 'Lead Growth' },
    ],
    features: [
      'Search engine optimization (SEO) for organic visibility',
      'Social media marketing across all major platforms',
      'Google Ads and Meta Ads campaign management',
      'Content marketing and blog strategy',
      'Email marketing automation and campaigns',
      'Analytics, reporting, and continuous optimization',
    ],
    benefits: [
      'Drive qualified traffic to your website consistently',
      'Build a loyal following on social media',
      'Achieve higher ROI on every marketing dollar spent',
      'Stay ahead of competitors with smart strategies',
    ],
  },
  {
    slug: 'seo',
    title: 'SEO',
    tagline: 'Rank higher, get found organically',
    category: 'Organic Reach',
    tags: ['Technical SEO', 'Content & Blogs', 'Local SEO', 'Backlinks'],
    icon: 'search',
    img: '/images/services/seo.jpg',
    desc: 'On-page, technical & content SEO — built to grow organic traffic month over month.',
    intro: 'We improve your search visibility through technical fixes, on-page optimisation, and content strategy — so your business gets found by the people already searching for what you offer.',
    stats: [
      { value: '40%', label: 'Organic Growth' },
      { value: '6-8', label: 'Blogs / Month' },
      { value: '90+', label: 'Site Speed Score' },
    ],
    features: [
      'Technical SEO audits & fixes',
      'On-page optimisation & keyword mapping',
      'Content strategy & blog writing',
      'Local SEO & Google Business Profile optimisation',
      'Backlink building & authority growth',
      'Monthly ranking & traffic reports',
    ],
    benefits: [
      'Attract visitors already searching for your services',
      'Reduce dependency on paid ads over time',
      'Build long-term organic authority in your niche',
      'Track clear, measurable ranking improvements',
    ],
  },
  {
    slug: 'google-ads',
    title: 'Google Ads',
    tagline: 'Search & Shopping campaigns that convert',
    category: 'Paid Search',
    tags: ['Search & Shopping', 'Keyword Research', 'Conversion Tracking', 'ROAS Focus'],
    icon: 'target',
    img: '/images/services/google-ads.jpg',
    desc: 'Search, Shopping & Display campaigns managed for ROAS, not just clicks.',
    intro: 'We plan, launch and optimise Google Ads campaigns — Search, Shopping, and Display — with a focus on qualified leads and return on ad spend, not vanity metrics.',
    stats: [
      { value: '3.2×', label: 'Avg ROAS' },
      { value: '48hr', label: 'Campaign Launch' },
      { value: '100%', label: 'Budget Transparency' },
    ],
    features: [
      'Search, Shopping & Display campaign setup',
      'Keyword research & competitor analysis',
      'Conversion tracking & attribution setup',
      'Ad copy testing & creative optimisation',
      'Budget pacing & bid strategy management',
      'Weekly performance reporting',
    ],
    benefits: [
      'Get in front of high-intent customers searching right now',
      'Full visibility into where every rupee goes',
      'Continuous optimisation instead of set-and-forget',
      'Scale spend confidently once campaigns prove ROI',
    ],
  },
  {
    slug: 'meta-ads',
    title: 'Meta Ads',
    tagline: 'Facebook & Instagram campaigns that scale',
    category: 'Paid Social',
    tags: ['Creative Testing', 'Retargeting', 'Pixel & CAPI', 'Audience Research'],
    icon: 'target',
    img: '/images/services/meta-ads.jpg',
    desc: 'Facebook & Instagram campaigns — creative-led, audience-tested, built to scale.',
    intro: 'We build and manage Meta Ads campaigns across Facebook and Instagram — from creative testing to audience targeting — to drive consistent leads and sales at a sustainable cost.',
    stats: [
      { value: '15K+', label: 'Monthly Reach' },
      { value: '40%', label: 'Lead Growth' },
      { value: '48hr', label: 'Campaign Launch' },
    ],
    features: [
      'Campaign strategy & audience research',
      'Creative testing (image, video, carousel)',
      'Retargeting & lookalike audience setup',
      'Meta Pixel & Conversions API integration',
      'Budget optimisation across ad sets',
      'Weekly performance & spend reports',
    ],
    benefits: [
      'Reach the right audience on the platforms they use daily',
      'Lower cost-per-lead through continuous creative testing',
      'Build a retargeting engine that recovers lost visitors',
      'Scale winning campaigns with confidence',
    ],
  },
  {
    slug: 'social-media-management',
    title: 'Social Media Management',
    tagline: 'Consistent content that builds a following',
    category: 'Social Presence',
    tags: ['Content Calendar', 'Reels & Posts', 'Community Mgmt', 'Growth Reports'],
    icon: 'share',
    img: '/images/services/social-media-management.jpg',
    desc: 'Content calendars, creatives & community management — built for consistent growth.',
    intro: 'We plan, design and post content across your social channels, engaging your community and keeping your brand consistently visible — so you can focus on running your business.',
    stats: [
      { value: '30+', label: 'Posts / Month' },
      { value: '5+', label: 'Platforms Managed' },
      { value: '100%', label: 'Content Ownership' },
    ],
    features: [
      'Monthly content calendar & planning',
      'Creative design for posts, reels & stories',
      'Community management & comment/DM responses',
      'Hashtag & caption strategy',
      'Influencer & UGC collaboration support',
      'Monthly growth & engagement reports',
    ],
    benefits: [
      'Stay consistently visible without managing it yourself',
      'Build a recognisable brand voice across platforms',
      'Turn followers into an engaged community',
      'Free up your time to focus on running the business',
    ],
  },
  {
    slug: 'graphic-design',
    title: 'Graphic Design',
    tagline: 'Visual identity that people remember',
    category: 'Brand & Design',
    tags: ['Brand Identity', 'Social Creatives', 'Print Design', 'UI/UX'],
    icon: 'palette',
    img: '/images/services/graphic-design.jpg',
    desc: 'Brand identities & creatives that stop the scroll — 200+ delivered, 48hr rush turnaround.',
    intro: 'Great design is the foundation of a memorable brand. Our design team creates visual identities and marketing materials that captivate your audience and communicate your brand message with clarity and impact.',
    stats: [
      { value: '200+', label: 'Creatives Delivered' },
      { value: '30+', label: 'Brands Designed' },
      { value: '48hr', label: 'Rush Turnaround' },
    ],
    features: [
      'Logo design and complete brand identity systems',
      'Social media creatives and ad banners',
      'Print design — brochures, flyers, business cards',
      'UI/UX design for web and mobile applications',
      'Packaging design and product mockups',
      'Presentation and pitch deck design',
    ],
    benefits: [
      'Stand out in crowded markets with distinctive visuals',
      'Build brand recognition and trust with consistency',
      'Increase engagement with scroll-stopping creatives',
      'Make a professional first impression every time',
    ],
  },
  {
    slug: 'ai-solutions',
    title: 'AI Solutions',
    tagline: 'Smart automation that works around the clock',
    category: 'AI Systems',
    tags: ['AI Chatbots', 'Process Automation', 'Predictive Analytics', 'Custom Integrations'],
    icon: 'cpu',
    img: '/images/services/ai-solutions.jpg',
    desc: 'Chatbots & automation running 24/7 — 60% time saved, 10+ AI tools integrated.',
    intro: 'Leverage the power of artificial intelligence to automate processes, gain insights, and create smarter workflows. We integrate cutting-edge AI tools into your business operations to save time and boost productivity.',
    stats: [
      { value: '24/7', label: 'Automated Support' },
      { value: '60%', label: 'Time Saved' },
      { value: '10+', label: 'AI Tools Integrated' },
    ],
    features: [
      'AI chatbots for customer support and lead generation',
      'Process automation to eliminate repetitive tasks',
      'AI-powered analytics and business intelligence',
      'Custom AI tool integration (OpenAI, machine learning APIs)',
      'Content generation and copywriting automation',
      'Predictive analytics for smarter decision-making',
    ],
    benefits: [
      'Reduce operational costs with intelligent automation',
      'Deliver faster customer responses around the clock',
      'Make data-backed decisions with predictive insights',
      'Future-proof your business with AI-ready infrastructure',
    ],
  },
  {
    slug: 'custom-software-development',
    title: 'Custom Software Development',
    tagline: 'Bespoke software built around how you work',
    category: 'Digital Engineering',
    tags: ['Custom Web Apps', 'Dashboards & Admin', 'API Integrations', 'SaaS / MVP'],
    icon: 'layers',
    img: '/images/services/custom-software-development.jpg',
    desc: 'Bespoke web apps & dashboards, fully yours — 15+ systems built, MVP live in 2–8 weeks.',
    intro: 'Off-the-shelf tools only get you so far. Our team designs and builds custom software from the ground up — web applications, internal tools, dashboards, and integrations — engineered around your real business processes so every feature earns its place.',
    stats: [
      { value: '100%', label: 'Full Ownership' },
      { value: '15+', label: 'Systems Built' },
      { value: '2-8wk', label: 'MVP Timeline' },
    ],
    features: [
      'Custom web & business applications built from scratch',
      'Internal tools, dashboards, and admin panels',
      'API development and third-party system integrations',
      'Database design and secure backend architecture',
      'SaaS product development from MVP to scale',
      'Legacy system modernization and re-platforming',
    ],
    benefits: [
      'Software that fits your exact process — no awkward workarounds',
      'Automate manual workflows unique to your business',
      'Own your platform fully, with no per-seat licensing limits',
      'Scale and extend easily as your requirements grow',
    ],
  },
];

export const getServiceBySlug = (slug) => servicesData.find(s => s.slug === slug);
